// ./src/utils/api/TravelReportService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { TravelTypeValue } from "@/config/travelTypes";

// ============================================
// GraphQL Query
// ============================================

const TRAVEL_REPORT_QUERY = gql`
  query ReportTravel($filter: TravelReportFilter!) {
    reportTravel(filter: $filter) {
      code
      data {
        # Period info
        dateFrom
        dateTo
        periodDays

        # Vehicles in report
        carIds
        vehiclesCount

        # Distance summary
        totalDistanceInPeriod
        filteredTripsDistance
        businessUsePercentage
        distanceUnit

        # Trip counts by type
        tripsByType {
          travelType
          tripsCount
          totalDistance
          percentageOfFiltered
        }

        # IRS Standard Mileage Method
        standardMileageDeduction {
          eligibleDistance
          distanceUnit
          byType {
            travelType
            distance
            rate
            rateCurrency
            deduction
            tierBreakdown {
              tierIndex
              distanceInTier
              rate
              amount
            }
          }
          totalDeduction
          currency
        }

        # CRA/IRS Actual Expense Method
        actualExpenseMethod {
          totalRefuelsCostHc
          totalRefuelsVolume
          totalMaintenanceCostHc
          totalOtherExpensesCostHc
          totalAllExpensesCostHc
          deductibleRefuelsCostHc
          deductibleMaintenanceCostHc
          deductibleOtherExpensesCostHc
          totalDeductibleCostHc
          volumeUnit
        }

        # Direct trip-linked totals
        linkedTotals {
          refuelsCostHc
          refuelsVolume
          expensesCostHc
          revenuesCostHc
          refuelsCount
          expensesCount
          revenuesCount
        }

        # Trip details table
        trips {
          id
          carId
          date
          endDate
          purpose
          destination
          travelType
          distance
          isRoundTrip
          activeMinutes
          totalMinutes
          refuelsTotal
          refuelsVolume
          expensesTotal
          revenuesTotal
          reimbursementRate
          reimbursementRateCurrency
          calculatedReimbursement
          tags {
            id
            tagName
            tagColor
          }
        }

        # Totals row (for table footer)
        tripsTotals {
          totalTrips
          totalDistance
          totalActiveMinutes
          totalTotalMinutes
          totalRefuelsCost
          totalRefuelsVolume
          totalExpensesCost
          totalRevenuesCost
          totalCalculatedReimbursement
        }

        # User preferences
        homeCurrency
        volumeUnit
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// ============================================
// Types
// ============================================

/**
 * Filter input for travel report (required fields: dateFrom, dateTo)
 */
export interface TravelReportFilter {
  /** Filter by specific vehicles (empty = all vehicles) */
  carId?: string[] | null;
  /** Filter by specific tags (empty = all) */
  tagId?: string[] | null;
  /** Filter by travel types: business, personal, medical, charity, commute */
  travelType?: TravelTypeValue[] | null;
  /** Start date (UTC, ISO 8601) - required */
  dateFrom: string;
  /** End date (UTC, ISO 8601) - required */
  dateTo: string;
}

/**
 * Tag attached to a trip
 */
export interface TripTag {
  /** Tag ID */
  id?: string;
  /** Tag name */
  tagName?: string;
  /** Tag color (hex code) */
  tagColor?: string;
}

/**
 * Individual trip detail for the report table
 */
export interface TripDetail {
  /** Trip ID */
  id?: string;
  /** Vehicle ID */
  carId?: string;
  /** Trip start date/time (ISO 8601) */
  date?: string;
  /** Trip end date/time (ISO 8601) */
  endDate?: string;
  /** Purpose of the trip */
  purpose?: string;
  /** Destination (from travels.destination or fallback from last record) */
  destination?: string;
  /** Travel type: business, personal, medical, charity, commute */
  travelType?: string;
  /** Distance in user's preferred unit */
  distance?: number;
  /** Whether this is a round trip */
  isRoundTrip?: boolean;
  /** Active driving/working time in minutes (for gig workers) */
  activeMinutes?: number;
  /** Total time including waiting in minutes */
  totalMinutes?: number;
  /** Total refuels cost linked to this trip (home currency) */
  refuelsTotal?: number;
  /** Total refuels volume linked to this trip (user's volume unit) */
  refuelsVolume?: number;
  /** Total expenses cost linked to this trip (home currency) */
  expensesTotal?: number;
  /** Total revenues linked to this trip (home currency) */
  revenuesTotal?: number;
  /** Reimbursement rate per distance unit */
  reimbursementRate?: number;
  /** Currency of the reimbursement rate */
  reimbursementRateCurrency?: string;
  /** Calculated reimbursement amount (rate × distance) */
  calculatedReimbursement?: number;
  /** Tags attached to this trip */
  tags?: TripTag[];
}

/**
 * Trips totals for table footer
 */
export interface TripsTotals {
  /** Total number of trips */
  totalTrips?: number;
  /** Total distance across all trips */
  totalDistance?: number;
  /** Total active minutes across all trips */
  totalActiveMinutes?: number;
  /** Total minutes (including waiting) across all trips */
  totalTotalMinutes?: number;
  /** Total refuels cost across all trips */
  totalRefuelsCost?: number;
  /** Total refuels volume across all trips */
  totalRefuelsVolume?: number;
  /** Total expenses cost across all trips */
  totalExpensesCost?: number;
  /** Total revenues cost across all trips */
  totalRevenuesCost?: number;
  /** Total calculated reimbursement across all trips */
  totalCalculatedReimbursement?: number;
}

/**
 * Breakdown by travel type
 */
export interface TravelTypeBreakdown {
  /** Travel type: business, personal, medical, charity, commute */
  travelType?: string;
  /** Number of trips of this type */
  tripsCount?: number;
  /** Total distance for this type (user's unit) */
  totalDistance?: number;
  /** Percentage of filtered trips distance */
  percentageOfFiltered?: number;
}

/**
 * Tier breakdown for tiered rate calculations (e.g., CRA style)
 */
export interface ReimbursementTierBreakdown {
  /** Tier index (0-based) */
  tierIndex?: number;
  /** Distance in this tier */
  distanceInTier?: number;
  /** Rate for this tier */
  rate?: number;
  /** Calculated amount for this tier */
  amount?: number;
}

/**
 * Deduction calculation for a specific travel type
 */
export interface StandardMileageByType {
  /** Travel type: business, personal, medical, charity, commute */
  travelType?: string;
  /** Distance in the rate's native unit */
  distance?: number;
  /** Primary rate (first tier rate) */
  rate?: number;
  /** Currency of the rate */
  rateCurrency?: string;
  /** Total deduction for this travel type */
  deduction?: number;
  /** Tier breakdown (only present for tiered rates like CRA) */
  tierBreakdown?: ReimbursementTierBreakdown[];
}

/**
 * Standard Mileage Deduction (IRS Method)
 * Calculates deduction based on IRS/CRA mileage rates.
 * Supports both flat rates (IRS) and tiered rates (CRA).
 */
export interface StandardMileageDeduction {
  /** Total eligible distance (business + medical + charity) */
  eligibleDistance?: number;
  /** Distance unit: km or mi */
  distanceUnit?: string;
  /** Breakdown by deductible travel type */
  byType?: StandardMileageByType[];
  /** Total deduction across all types */
  totalDeduction?: number;
  /** Currency of the deduction */
  currency?: string;
}

/**
 * Actual Expense Method (CRA/IRS Alternative)
 * Calculates deductible expenses based on business use percentage.
 * Total expenses × (business km / total km) = deductible amount
 */
export interface ActualExpenseMethod {
  /** Total refuels cost in period (all records, home currency) */
  totalRefuelsCostHc?: number;
  /** Total refuels volume in period (user's volume unit) */
  totalRefuelsVolume?: number;
  /** Total maintenance expenses (where expense_kinds.is_it_maintenance = true) */
  totalMaintenanceCostHc?: number;
  /** Total other expenses (non-maintenance) */
  totalOtherExpensesCostHc?: number;
  /** Sum of all expenses (refuels + maintenance + other) */
  totalAllExpensesCostHc?: number;
  /** Deductible refuels (totalRefuelsCostHc × businessUsePercentage) */
  deductibleRefuelsCostHc?: number;
  /** Deductible maintenance */
  deductibleMaintenanceCostHc?: number;
  /** Deductible other expenses */
  deductibleOtherExpensesCostHc?: number;
  /** Total deductible amount */
  totalDeductibleCostHc?: number;
  /** Volume unit: L, gal-us, gal-uk */
  volumeUnit?: string;
}

/**
 * Totals for expenses directly linked to the filtered trips via travel_id
 */
export interface LinkedTotals {
  /** Refuels cost linked to filtered trips (home currency) */
  refuelsCostHc?: number;
  /** Refuels volume linked to filtered trips */
  refuelsVolume?: number;
  /** Expenses cost linked to filtered trips (home currency) */
  expensesCostHc?: number;
  /** Revenues cost linked to filtered trips (home currency) */
  revenuesCostHc?: number;
  /** Number of refuel records linked */
  refuelsCount?: number;
  /** Number of expense records linked */
  expensesCount?: number;
  /** Number of revenue records linked */
  revenuesCount?: number;
}

/**
 * Travel Report for IRS/CRA Tax Compliance
 *
 * Provides two deduction calculation methods:
 * 1. Standard Mileage Method (IRS): Multiply eligible miles by IRS rate
 * 2. Actual Expense Method (CRA): Total expenses × business use percentage
 *
 * The report shows:
 * - All trips matching the filter criteria
 * - Business use percentage based on tracked vs total distance
 * - Deduction calculations for tax filing
 * - Expenses directly linked to each trip
 */
export interface TravelReport {
  // =========================================================================
  // Period Info
  // =========================================================================
  /** Report start date (ISO 8601) */
  dateFrom?: string;
  /** Report end date (ISO 8601) */
  dateTo?: string;
  /** Number of days in the period */
  periodDays?: number;

  // =========================================================================
  // Vehicles in Report
  // =========================================================================
  /** IDs of vehicles included in this report */
  carIds?: string[];
  /** Number of vehicles included */
  vehiclesCount?: number;

  // =========================================================================
  // Distance Summary
  // =========================================================================
  /**
   * Total distance traveled by all selected vehicles in the period.
   * Calculated from ALL expense_bases records (max odometer - min odometer per car).
   */
  totalDistanceInPeriod?: number;

  /**
   * Sum of distances for filtered trips only.
   */
  filteredTripsDistance?: number;

  /**
   * Business use percentage: (filteredTripsDistance / totalDistanceInPeriod) × 100
   * Used for actual expense method calculation.
   */
  businessUsePercentage?: number;

  /** Distance unit: km or mi */
  distanceUnit?: string;

  // =========================================================================
  // Trip Counts by Type
  // =========================================================================
  /** Breakdown of trips by travel type with counts and distances */
  tripsByType?: TravelTypeBreakdown[];

  // =========================================================================
  // IRS Standard Mileage Method
  // =========================================================================
  /**
   * Standard mileage deduction calculation (IRS method).
   * Uses official IRS/CRA mileage rates for deductible travel types.
   * Null if no deductible trips exist.
   */
  standardMileageDeduction?: StandardMileageDeduction;

  // =========================================================================
  // CRA/IRS Actual Expense Method
  // =========================================================================
  /**
   * Actual expense method calculation (CRA/IRS alternative).
   * Shows total expenses and deductible portion based on business use percentage.
   */
  actualExpenseMethod?: ActualExpenseMethod;

  // =========================================================================
  // Direct Trip-Linked Totals
  // =========================================================================
  /**
   * Totals for expenses directly linked to the filtered trips.
   * Only includes expense_bases records where travel_id matches a filtered trip.
   */
  linkedTotals?: LinkedTotals;

  // =========================================================================
  // Trip Details Table
  // =========================================================================
  /** Individual trip records matching the filter */
  trips?: TripDetail[];

  // =========================================================================
  // Totals Row (for table footer)
  // =========================================================================
  /** Aggregated totals for the trips table footer */
  tripsTotals?: TripsTotals;

  // =========================================================================
  // User Preferences
  // =========================================================================
  /** User's home currency code */
  homeCurrency?: string;
  /** User's volume unit (L, gal-us, gal-uk) */
  volumeUnit?: string;
}

// ============================================
// Service Class
// ============================================

/**
 * TravelReportService handles API calls for the travel/mileage report.
 *
 * This report provides comprehensive travel tracking for tax compliance with:
 * - Trip details with linked expenses and revenues
 * - Business use percentage calculation
 * - Two deduction methods: Standard Mileage (IRS) and Actual Expense (CRA)
 * - Travel type breakdowns (business, personal, medical, charity, commute)
 * - Support for tiered rates (CRA) and flat rates (IRS)
 *
 * @example
 * // Get travel report for a tax year
 * const result = await TravelReportService.get({
 *   dateFrom: '2025-01-01',
 *   dateTo: '2025-12-31',
 *   travelType: ['business', 'medical', 'charity']
 * });
 *
 * @example
 * // Get business trips for specific vehicles
 * const result = await TravelReportService.get({
 *   carId: ['car-uuid-123', 'car-uuid-456'],
 *   travelType: ['business'],
 *   dateFrom: '2025-01-01',
 *   dateTo: '2025-03-31'
 * });
 */
export class TravelReportService {
  /**
   * Get travel report with required date range and optional filters.
   *
   * @param filter - Filter with required dateFrom/dateTo and optional carId, tagId, travelType
   * @returns OpResult containing TravelReport data
   *
   * @example
   * // Get report for tax year with all travel types
   * const result = await TravelReportService.get({
   *   dateFrom: '2025-01-01',
   *   dateTo: '2025-12-31'
   * });
   *
   * @example
   * // Get business-only trips for Q1
   * const result = await TravelReportService.get({
   *   dateFrom: '2025-01-01',
   *   dateTo: '2025-03-31',
   *   travelType: ['business']
   * });
   *
   * @example
   * // Access deduction calculations
   * const result = await TravelReportService.get({
   *   dateFrom: '2025-01-01',
   *   dateTo: '2025-12-31'
   * });
   * const report = result.data?.[0];
   *
   * // Standard Mileage Method (IRS)
   * console.log(`Total deduction: ${report?.standardMileageDeduction?.totalDeduction}`);
   *
   * // Actual Expense Method (CRA)
   * console.log(`Business use: ${report?.businessUsePercentage}%`);
   * console.log(`Deductible: ${report?.actualExpenseMethod?.totalDeductibleCostHc}`);
   */
  static async get(filter: TravelReportFilter): Promise<OpResult> {
    const result = await ApiService.executeQuery(TRAVEL_REPORT_QUERY, {
      filter,
    });
    return result;
  }
}